import { z } from 'zod';

/**
 * Platform-wide audit trail. Every entry answers three questions: what was
 * changed, who changed it, and what the values were before and after.
 */

/**
 * Who made the change. The distinction that matters operationally is
 * person vs. our own AI tooling (the MCP admin key) vs. unattended
 * background work.
 */
export const activityActorTypeSchema = z.enum(['user', 'mcp', 'system']);
export type ActivityActorType = z.infer<typeof activityActorTypeSchema>;

export const ACTIVITY_ACTOR_TYPES = activityActorTypeSchema.options;

export const ACTIVITY_ACTOR_TYPE_LABELS: Record<ActivityActorType, string> = {
  user: 'User',
  mcp: 'AI / MCP',
  system: 'System',
};

/** How the change reached the platform. */
export const activitySourceSchema = z.enum(['api', 'mcp', 'workflow', 'cron', 'system']);
export type ActivitySource = z.infer<typeof activitySourceSchema>;

export const ACTIVITY_SOURCE_LABELS: Record<ActivitySource, string> = {
  api: 'Dashboard / API',
  mcp: 'MCP',
  workflow: 'Workflow',
  cron: 'Scheduled',
  system: 'System',
};

/**
 * Entity types are open-ended on purpose — a module gets audited by passing
 * its own name, no schema change required. These are the ones known today.
 */
export const ACTIVITY_ENTITY_TYPES = [
  'configuration',
  'workflow',
  'campaign',
  'custom_tool',
  'chatbot_tool',
  'scraper',
  'user',
  'setting',
  'review_config',
  'quality_config',
  'classifier_config',
  'agent_definition',
  'whatsapp_template',
  'outcome_definition',
  'booking',
  'contact',
] as const;

export const ACTIVITY_ENTITY_TYPE_LABELS: Record<string, string> = {
  configuration: 'Configuration',
  workflow: 'Workflow',
  campaign: 'Campaign',
  custom_tool: 'Custom tool',
  chatbot_tool: 'Chatbot tool',
  scraper: 'Scraper',
  user: 'User',
  setting: 'Setting',
  review_config: 'Review config',
  quality_config: 'Quality config',
  classifier_config: 'Classifier config',
  agent_definition: 'Agent definition',
  whatsapp_template: 'WhatsApp template',
  outcome_definition: 'Outcome',
  booking: 'Booking',
  contact: 'Contact',
};

/** Actions are open-ended too; these cover the common CRUD verbs. */
export const ACTIVITY_ACTIONS = [
  'create',
  'update',
  'delete',
  'activate',
  'deactivate',
  'lock',
  'unlock',
  'restore',
  'create_version',
  'activate_version',
  'delete_version',
] as const;

export const ACTIVITY_ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  activate: 'Activated',
  deactivate: 'Deactivated',
  lock: 'Locked',
  unlock: 'Unlocked',
  restore: 'Restored',
  create_version: 'Version created',
  activate_version: 'Version activated',
  delete_version: 'Version deleted',
};

/** Actions that removed something — rendered in a destructive tone. */
export const DESTRUCTIVE_ACTIVITY_ACTIONS = new Set(['delete', 'delete_version', 'deactivate']);

/** One field-level difference, `path` being a dotted/indexed JSON path. */
export const activityChangeSchema = z.object({
  path: z.string(),
  before: z.unknown().nullable().optional(),
  after: z.unknown().nullable().optional(),
});

export type ActivityChange = z.infer<typeof activityChangeSchema>;

export const activityLogSchema = z.object({
  id: z.string().uuid(),

  entity_type: z.string(),
  entity_id: z.string().nullable().optional(),
  entity_label: z.string().nullable().optional(),
  action: z.string(),

  actor_type: activityActorTypeSchema,
  actor_id: z.string().nullable().optional(),
  actor_name: z.string().nullable().optional(),
  actor_email: z.string().nullable().optional(),
  actor_role: z.string().nullable().optional(),

  summary: z.string().nullable().optional(),
  changed_fields: z.array(z.string()),
  before: z.unknown().nullable().optional(),
  after: z.unknown().nullable().optional(),
  changes: z.array(activityChangeSchema).nullable().optional(),

  request_id: z.string().nullable().optional(),
  ip: z.string().nullable().optional(),
  user_agent: z.string().nullable().optional(),
  method: z.string().nullable().optional(),
  path: z.string().nullable().optional(),
  source: activitySourceSchema.nullable().optional(),
  status: z.string(),
  error: z.string().nullable().optional(),
  metadata: z.unknown().nullable().optional(),

  created_at: z.string(),
});

export type ActivityLog = z.infer<typeof activityLogSchema>;

/**
 * The list endpoint. `entity_id` narrows to one row's history (the panel on a
 * configuration page); everything else is for the platform-wide feed.
 */
export const activityLogQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  entity_type: z.string().max(64).optional(),
  entity_id: z.string().max(128).optional(),
  action: z.string().max(32).optional(),
  actor_type: activityActorTypeSchema.optional(),
  actor_id: z.string().max(128).optional(),
  source: activitySourceSchema.optional(),
  /// Only entries that touched this top-level field, e.g. 'prompt_template'.
  field: z.string().max(128).optional(),
  /// Matches entity label, actor name/email and the summary line.
  search: z.string().max(200).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  /// Hide the coarse interceptor entries, leaving only field-level diffs.
  detailed_only: z.coerce.boolean().optional(),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type ActivityLogQuery = z.infer<typeof activityLogQuerySchema>;

/** Distinct values behind the filter dropdowns. */
export const activityLogFacetsSchema = z.object({
  entity_types: z.array(z.object({ value: z.string(), count: z.number() })),
  actions: z.array(z.object({ value: z.string(), count: z.number() })),
  actors: z.array(
    z.object({
      actor_id: z.string().nullable(),
      actor_name: z.string().nullable(),
      actor_email: z.string().nullable(),
      actor_type: activityActorTypeSchema,
      count: z.number(),
    }),
  ),
});

export type ActivityLogFacets = z.infer<typeof activityLogFacetsSchema>;
